import { useEffect, useRef, useState, type ReactNode } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { dirForLanguage, getLanguage, RENDERABLE_LANGUAGES } from '../../lib/languages';
import {
  acceptLegalNotice,
  isLegalNoticeRefusal,
  LEGAL_NOTICE_VERSION,
  owesLegalNotice,
} from '../../lib/legalNotice';
import { fetchProfile, PROFILE_QUERY_KEY, PROFILE_STALE_TIME, type MyProfile } from '../../lib/profile';

/** The notice's body paragraphs, in order (`legalNotice.p1` …). */
const PARAGRAPHS = ['p1', 'p2', 'p3', 'p4', 'p5'] as const;

/**
 * Nothing in the app renders until the account has confirmed the confidentiality notice.
 *
 * It wraps every signed-in route (SessionGate), the player's own page and onboarding included, and
 * while the profile says the notice is owed it renders the notice INSTEAD of the page — not over
 * it — so no page mounts and nothing is fetched behind it. The gateway refuses the API too
 * (LegalNoticeMiddleware), so this is the honest face of that refusal rather than the boundary.
 *
 * Acceptance is the server's, read from the profile on every load: closing the tab, signing out or
 * switching device brings the notice back until it is confirmed, and never again after.
 */
export function LegalNoticeGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: profile, isPending } = useQuery<MyProfile>({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: fetchProfile,
    staleTime: PROFILE_STALE_TIME,
    retry: 1,
    retryDelay: 2_000,
  });

  // The gateway refusing a request for the notice means the profile in hand is out of date (a
  // reworded notice went live mid-session): re-read it, and the notice comes up.
  useEffect(() => {
    const id = api.interceptors.response.use(undefined, (error: unknown) => {
      if (isLegalNoticeRefusal(error)) void queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEY });
      return Promise.reject(error);
    });
    return () => api.interceptors.response.eject(id);
  }, [queryClient]);

  // Until the profile answers, show nothing rather than a page the notice may have to replace. A
  // profile that FAILS renders the page as before: every page already degrades on that, and the
  // gateway still refuses the data if the notice is owed.
  if (isPending) return null;
  if (owesLegalNotice(profile)) {
    return <LegalNoticeDialog serverVersion={profile?.legalNotice?.version} />;
  }
  return <>{children}</>;
}

type Status = 'idle' | 'saving' | 'failed' | 'outdated';

function LegalNoticeDialog({ serverVersion }: { serverVersion?: string }) {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const bodyRef = useRef<HTMLDivElement>(null);

  // Opens in the app's language (the club's), and can be read in the other: a notice someone is
  // bound by must be readable to them. The confirmation records the language it was shown in.
  const [lang, setLang] = useState(() =>
    RENDERABLE_LANGUAGES.includes(i18n.language) ? i18n.language : RENDERABLE_LANGUAGES[0],
  );
  const { t } = useTranslation(undefined, { lng: lang });

  // This build carries the text of LEGAL_NOTICE_VERSION only. When the server asks for another one,
  // confirming here would record a text the user never saw — so the only way on is a reload.
  const [status, setStatus] = useState<Status>(() =>
    serverVersion && serverVersion !== LEGAL_NOTICE_VERSION ? 'outdated' : 'idle',
  );

  const switchLanguage = async (code: string) => {
    if (code === lang) return;
    await i18n.loadLanguages(code);
    setLang(code);
  };

  const confirmNotice = async () => {
    setStatus('saving');
    try {
      const { acceptedAt } = await acceptLegalNotice(lang);
      queryClient.setQueryData<MyProfile>(PROFILE_QUERY_KEY, (old) =>
        old ? { ...old, legalNotice: { version: LEGAL_NOTICE_VERSION, required: false, acceptedAt } } : old,
      );
      // Anything read while the notice was up may be holding the gateway's refusal.
      void queryClient.invalidateQueries();
    } catch (error) {
      setStatus((error as { response?: { status?: number } })?.response?.status === 409 ? 'outdated' : 'failed');
    }
  };

  const dir = dirForLanguage(lang);

  return (
    <AlertDialog.Root open>
      <AlertDialog.Portal>
        {/* Opaque, not a scrim: there is no page underneath to see, by design. */}
        <AlertDialog.Overlay className="fixed inset-0 z-[90] bg-[var(--bg-page)]" />
        <AlertDialog.Content
          dir={dir}
          lang={lang}
          // Nothing dismisses it: no Escape, no outside click, no close button, no sign-out — the
          // only way on is to confirm (decision 2026-10-12).
          onEscapeKeyDown={(e) => e.preventDefault()}
          // Focus the text, not the button — Enter on arrival must not be a confirmation.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            bodyRef.current?.focus();
          }}
          // Sized by the DYNAMIC viewport (dvh), never vh: on iOS Safari 100vh is the height with the
          // URL bar and toolbar hidden, so a box sized by it ran under both and cut off the title and
          // the confirm button. On a phone it is a full-screen sheet clear of the notch and home bar;
          // from `sm` up, a centred card. Only the text scrolls — the title and button stay in view.
          className="fixed inset-x-0 top-0 z-[100] flex h-[100dvh] flex-col bg-[var(--bg-card)] pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] sm:inset-x-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:w-[600px] sm:max-w-[calc(100vw-2rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border sm:border-white/5 sm:p-0 sm:shadow-2xl"
        >
          <div className="flex shrink-0 items-start gap-3 border-b border-white/5 p-4 sm:p-5">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-[var(--club-ink)]" aria-hidden />
            <AlertDialog.Title className="flex-1 text-base font-semibold text-[var(--text-primary)]">
              {t('legalNotice.title')}
            </AlertDialog.Title>
            <div className="flex shrink-0 gap-1" role="group" aria-label={t('legalNotice.languageLabel')}>
              {RENDERABLE_LANGUAGES.map((code) => (
                <button
                  key={code}
                  type="button"
                  lang={code}
                  aria-pressed={code === lang}
                  onClick={() => void switchLanguage(code)}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs transition-colors',
                    code === lang
                      ? 'bg-white/10 font-semibold text-[var(--text-primary)]'
                      : 'text-[var(--text-secondary)] hover:bg-white/5',
                  )}
                >
                  {getLanguage(code).nativeName}
                </button>
              ))}
            </div>
          </div>

          <div
            ref={bodyRef}
            tabIndex={-1}
            className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4 text-sm sm:p-5 leading-relaxed text-[var(--text-secondary)] focus:outline-none"
          >
            <AlertDialog.Description className="font-medium text-[var(--text-primary)]">
              {t('legalNotice.intro')}
            </AlertDialog.Description>
            {PARAGRAPHS.map((key) => (
              <p key={key}>{t(`legalNotice.${key}`)}</p>
            ))}
            <p className="font-medium text-[var(--text-primary)]">{t('legalNotice.agreement')}</p>
          </div>

          <div className="shrink-0 border-t border-white/5 p-4 sm:p-5">
            {status === 'failed' && (
              <p role="alert" className="mb-3 text-sm text-red-400">
                {t('legalNotice.saveFailed')}
              </p>
            )}
            {status === 'outdated' && (
              <p role="alert" className="mb-3 text-sm text-[var(--text-primary)]">
                {t('legalNotice.outdated')}
              </p>
            )}
            <div className="flex items-center justify-end gap-3">
              {status === 'outdated' ? (
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="text-on-club w-full rounded-lg bg-[var(--club-primary,var(--c-accent))] px-4 py-3 text-sm font-medium sm:w-auto sm:py-2 transition-opacity hover:opacity-90"
                >
                  {t('legalNotice.reload')}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={status === 'saving'}
                  onClick={() => void confirmNotice()}
                  // Ink follows the fill, never text-white (see ConfirmHost).
                  className="text-on-club w-full rounded-lg bg-[var(--club-primary,var(--c-accent))] px-4 py-3 text-sm font-medium sm:w-auto sm:py-2 transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  {status === 'saving' ? t('legalNotice.saving') : t('legalNotice.confirm')}
                </button>
              )}
            </div>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
